import { Badge, Button, humanizedMarketplaceName, humanizedProviderName, Icon } from "@deligoo/ui"
import clsx from "clsx"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/Card"
import { DriverHeader } from "@/components/DriverHeader"
import panelCls from "@/components/Panel/Panel.module.scss"
import { Order, OrderPoint } from "@/types"
import { formatDelay, formatPrice, formatTime, getIconFor, OrderUtils } from "@/utils"

import cls from "./OrderDetails.module.scss"

type OrderDetailsProps = {
  type: "pickup" | "delivery"
  order: Order
}

export const OrderDetails = ({ order, type }: OrderDetailsProps) => {
  const { t } = useTranslation()

  const point = order[type]

  const state = point ? point.state : order.state
  const pointDelay = point && OrderUtils.getPointDelay(point)

  return (
    <Card className={panelCls.card}>
      <div className={clsx(cls.status, cls[state])}>
        <Icon name={getIconFor(state)} className={cls.icon} />
        <span className={cls.content}>{t(`orders:${state}`)}</span>

        {point && (
          <span className={cls.time}>
            <span>{formatTime(OrderUtils.getPointTime(point))}</span>
            {OrderUtils.shouldShowDelay(point, pointDelay) && (
              <span className={`text-${OrderUtils.getPointDelayColor(pointDelay)}`}>
                {formatDelay(pointDelay!, " (", ")")}
              </span>
            )}
          </span>
        )}
      </div>

      <Card.Section>
        <DriverHeader driverId={order.driver_id} orderId={order.id} />
      </Card.Section>

      {point && <AboutPointSection point={point} />}

      <BadgesSection order={order} />

      <TimesSection order={order} />

      <CommentsSection order={order} />
    </Card>
  )
}

const AboutPointSection = ({ point }: { point: OrderPoint }) => {
  const { name, address, phone } = point

  return (
    <Card.Section>
      <div className={cls.about}>
        <div>
          <div className={cls.name}>{name || phone}</div>
          <div className={cls.address}>{address}</div>
        </div>

        <Button
          as="a"
          size="small"
          color="primary"
          variation="text"
          href={`tel:${phone}`}
          iconProps={{ name: "phone" }}
        />
      </div>
    </Card.Section>
  )
}

const BadgesSection = ({ order }: { order: Order }) => {
  const { t } = useTranslation()

  const { payment_form, rating } = order

  return (
    <Card.Section>
      <Badge color="primary" variation="accented" size="small" iconProps={{ name: "credit-card" }}>
        {t(`orders:${payment_form}`)}
      </Badge>

      {rating && (
        <Badge color="alert" iconProps={{ name: "star" }}>
          {rating}
        </Badge>
      )}
    </Card.Section>
  )
}

const TimesSection = ({ order }: { order: Order }) => {
  const estimatedDeliveryTime = OrderUtils.getEstimatedDeliveryTime(order)
  const realDeliveryTime = OrderUtils.getRealDeliveryTime(order)

  const { t } = useTranslation("orders")

  const humanizedProvider = order.marketplace_kind
    ? humanizedMarketplaceName[order.marketplace_kind]
    : humanizedProviderName[order.provider]

  return (
    <Card.Section>
      <div className={cls.times}>
        <OrderInfo label="Czas dodania" info={formatTime(order.created_at)} real="real" />
        {order.pickup_in && order.pickup_at && (
          <OrderInfo
            label="Czas kompletacji"
            info={`${order.pickup_in} min`}
            real={formatTime(order.pickup_at) || "-"}
          />
        )}
        {order.pickup && (
          <OrderInfo
            label="Czas odbioru"
            info={formatTime(order.pickup.estimated_time) || "-"}
            real={formatTime(order.pickup.finished_at) || "-"}
          />
        )}
        {order.delivery && (
          <OrderInfo
            label="Czas doręczenia"
            info={formatTime(order.delivery.estimated_time) || "-"}
            real={formatTime(order.delivery.finished_at) || "-"}
          />
        )}
        {order.pickup && order.delivery && (
          <OrderInfo
            label="Czas przewozu"
            info={estimatedDeliveryTime !== null ? `${estimatedDeliveryTime} min` : ""}
            real={realDeliveryTime !== null ? `${realDeliveryTime} min` : "-"}
          />
        )}
        {order.distance !== null && <OrderInfo label="Dystans" info={`${OrderUtils.getRealOrderDistance(order)} km`} />}
        <OrderInfo label="Kwota płatności" info={`${formatPrice(order.full_price_subunit, { subunit: true })} zł`} />
        <OrderInfo label="Ilość opakowań" info={OrderUtils.getPackagesCount(order.packages)} />
        <OrderInfo label="Rodzaj serwisu" info={order.service_type} />
        <OrderInfo
          label="Rodzaj opakowania"
          info={Object.keys(order.packages)
            .map((packageType) => t(`package_type.${packageType}`))
            .join(", ")}
        />
        <OrderInfo label="Źródło" info={humanizedProvider || order.provider} />
      </div>
    </Card.Section>
  )
}

type OrderInfoProps = {
  label: string
  info: number | string
  real?: string
}

const OrderInfo = ({ label, info, real }: OrderInfoProps) => {
  return (
    <div className={cls.time}>
      <span className="text-muted">{label}</span>
      <span className="font-weight-bold mt-1.5">
        {info} {real && `[${real}]`}
      </span>
    </div>
  )
}

const CommentsSection = ({ order }: { order: Order }) => {
  const { client_comment, customer_comment, order_comment } = order

  if (!client_comment && !customer_comment && !order_comment) return null

  return (
    <Card.Section>
      <h4 className="h100">Uwagi:</h4>
      <div className={cls.comments}>
        {client_comment && <span className={cls.comment}>Od lokalizacji: {client_comment}</span>}

        {customer_comment && <span className={cls.comment}>Od klienta: {customer_comment}</span>}

        {order_comment && <span className={cls.comment}>Do zamówienia: {order_comment}</span>}
      </div>
    </Card.Section>
  )
}
